import { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import { PapsContext } from '../PapsContext';
import { AppTimestampContext } from '../AppTimestampContext';
import { useCacheDataManager } from './cacheDataManager';
import { shouldUseCache } from './useHelpers';
import { DatabaseContext } from '../DatabaseContext';

export const useCachedData = ({
  cache_groupType,
  cache_key,
  fetchers = [],
  updateLocalState,
  postProcess,
}) => {


  const cdm = useCacheDataManager();

  const { paps_state } = useContext(PapsContext) ?? {};
  const { pathViews } = paps_state ?? {};

  const { database_fns } = useContext(DatabaseContext) ?? {};
  const { isProdDb } = database_fns ?? {};

  const { appTimestamp_state } = useContext(AppTimestampContext) ?? {};
  const { timestampInfo } = appTimestamp_state ?? {};
  const { timestamps, timestampsFetched } = timestampInfo ?? {};

  // ✅ single source of truth
  const currentTimestamp_db = timestamps?.[cache_groupType]?.[cache_key];

  // ✅ track last fetched timestamp (prevents duplicate fetches)
  const [lastFetchedTimestamp, setLastFetchedTimestamp] = useState(null);

  useEffect(() => {

    // basic guards
    if (!database_fns || !cache_key || !timestampsFetched) return;
    // if (_.isNil(currentTimestamp)) return;

    // ✅ prevent re-fetching same timestamp
    if (lastFetchedTimestamp === currentTimestamp_db) return;


    (async () => {

      const cached = await cdm.getSubItem(cache_groupType, cache_key);

      // ✅ use cache if valid
      if (!isProdDb && !_.isEmpty(cached) && shouldUseCache(cached?._timestamp, currentTimestamp_db, cache_groupType)) {
        // console.log('✅🗄️', cache_groupType, cache_key, cached)
        updateLocalState(cached);
        setLastFetchedTimestamp(currentTimestamp_db);
        return;
      }

      console.log('➡️🔥', cache_groupType, cache_key);

      const fetchAndGet = async ({ key, getConfigFn }) =>
        new Promise((resolve, reject) => {
          try {
            const callback = (data) => resolve([key, _.cloneDeep(data)]);
            database_fns.get_data(getConfigFn(pathViews, callback));
          } catch (err) {
            reject(err);
          }
        });

      try {
        const results = await Promise.all(fetchers.map(fetchAndGet));

        const mergedData = _(results)
          .compact()
          .fromPairs()
          .pickBy((v) => !_.isNil(v) && !_.isEmpty(v))
          .value();

        let finalData = mergedData;

        // optional post-process
        if (typeof postProcess === 'function') {
          finalData = postProcess(_.cloneDeep(mergedData)) ?? mergedData;
        }

        // store in cache
        if (!isProdDb) {
          await cdm.mergeToGroup(cache_groupType, cache_key, finalData, null, true);
        }

        updateLocalState(finalData);

        // ✅ mark this timestamp as handled
        setLastFetchedTimestamp(currentTimestamp_db);

      } catch (err) {
        console.error('❌ Failed to fetch cached remote data', err);
      }

    })();
  }, [
    cache_groupType, cache_key, timestampsFetched, database_fns, pathViews, isProdDb, currentTimestamp_db,   // 🔥 key dependency
  ]);

  return {
    currentTimestamp: currentTimestamp_db,
    lastFetchedTimestamp
  };
};
